import { RejectionDSAType } from "@nkr/enums/rejection-dsa-type";
import { IHlaAllele } from "@nkr/models/hla";
import React, { useEffect, useState } from "react";
import { Alert, Checkbox, Col, ControlLabel, FormControl, FormGroup, Panel, Row } from "react-bootstrap";

export interface IProps {
    type: RejectionDSAType
    donorTyping: IHlaAllele[],
    disabled: boolean,
    dsaValid: boolean,
    persistentDsaOtherValid: boolean,
    data: { allele: string, mfi: number }[],
    otherDetails: { nonHla: boolean, other: boolean, otherDetails: string },
    onSetDsa: (dsaList: { allele: string, mfi: number }[]) => void,
    onSetDsaOther: (nonHla: boolean, other: boolean, otherDetails: string) => void
}

const IdentifiedDSA = (props: IProps) => {
    useEffect(() => {
        if (props.data != null && props.data.length > 0) {
            setAlleles(props.data);
        }

        if (props.otherDetails != null) {
            setNonAlleleOptions(props.otherDetails);
        }

    }, [])

    let [alleles, setAlleles] = useState<{ allele: string, mfi: number }[]>([]);

    let [nonAlleleOptions, setNonAlleleOptions] = useState<{ nonHla: boolean, other: boolean, otherDetails: string }>({
        nonHla: null,
        other: null,
        otherDetails: ''
    })

    const handleAlleleSelect = (e: React.FormEvent<HTMLInputElement>) => {
        let allele = e.currentTarget.name;

        let currentList = alleles.filter(x => x.allele == allele);

        if (currentList.length > 0) {
            let newList = alleles.filter(x => x.allele != allele);
            setAlleles(prevState => newList);
            props.onSetDsa(newList);
        } else {
            currentList = alleles;
            let treatmentObject = { allele: allele, mfi: null };
            let newList = [...currentList, treatmentObject]
            setAlleles(prevState => newList);
            props.onSetDsa(newList);
        }
    }

    const handleAlleleMfiChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let { name: allele, value } = e.currentTarget;
        let currentList = alleles.filter(x => x.allele == allele);

        if (currentList.length > 0) {
            let newList = alleles.filter(x => x.allele != allele);
            let alleleObject = { allele: allele, mfi: parseInt(value) };
            newList = [...newList, alleleObject];
            setAlleles(prevState => [...newList]);
            props.onSetDsa(newList);
        }
    };

    const handleSetOtherOptions = (e: React.FormEvent<HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (name == 'nonHla') {
            let nonHla = !nonAlleleOptions.nonHla;
            setNonAlleleOptions(prevState => ({ ...prevState, nonHla }))
            props.onSetDsaOther(nonHla, nonAlleleOptions.other, nonAlleleOptions.otherDetails);
        } else if (name == 'other') {
            let other = !nonAlleleOptions.other;
            setNonAlleleOptions(prevState => ({ ...prevState, other }))
            props.onSetDsaOther(nonAlleleOptions.nonHla, other, nonAlleleOptions.otherDetails);
        } else {
            let otherDetails = value;
            setNonAlleleOptions(prevState => ({ ...prevState, otherDetails: value }));
            props.onSetDsaOther(nonAlleleOptions.nonHla, nonAlleleOptions.other, value);
        }
    };

    const handleSetOtherOptionsForm = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;

        setNonAlleleOptions(prevState => ({ ...prevState, [name]: value }));

        props.onSetDsaOther(nonAlleleOptions.nonHla, nonAlleleOptions.other, nonAlleleOptions.otherDetails);
    };

    const dsaError = () => {
        if (!props.dsaValid || !props.persistentDsaOtherValid) {
            return (
                <Alert bsSize='small' bsStyle='danger'>
                    Either a DSA choice has not been selected or an MFI is too low
                </Alert>
            )
        }

        return <></>;
    };

    if (props.donorTyping == null || props.donorTyping == undefined) {
        return <></>;
    }

    let newDonorMapping = props.donorTyping.reduce((groups, x) => {
        let group = x.gene;
        let alleleState = alleles.find(a => a.allele === x.hlaString);

        if (!groups[group]) {
            groups[group] = [];
        }

        groups[group].push({ allele: x.hlaString, mfi: alleleState?.mfi ?? null });

        return groups;
    }, {} as Record<string, { allele: string, mfi: number }[]>);

    let alleleIds = alleles?.map(x => x.allele);

    return (
        <>
            {dsaError()}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(newDonorMapping).map(([group, typings]) => (
                    <fieldset key={group} style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '6px 10px', margin: 0, flexBasis: 'calc(25% - 8px)' }}>
                        <legend style={{ fontSize: '12px', fontWeight: 'bold', width: 'auto', marginBottom: '4px', padding: '0 4px', borderBottom: 'none' }}>
                            {group}
                        </legend>
                        {typings.map((x, index) => (
                            <div key={'type-' + index}>
                                <input
                                    style={{ marginRight: '5px' }}
                                    type="checkbox"
                                    name={x.allele}
                                    disabled={props.disabled}
                                    checked={alleleIds.includes(x.allele)}
                                    onChange={handleAlleleSelect}
                                />
                                <span style={{ margin: '4px' }}>
                                    {x.allele}
                                </span>
                                <span style={{ marginLeft: '10px', marginRight: '1px' }}>
                                    MFI:
                                 </span>
                                <FormControl
                                    style={{ display: 'inline-block', width: 50, marginLeft: '1px', borderColor: props.dsaValid || !alleleIds.includes(x.allele) || (x.mfi ?? 0 > 0) ? undefined : '#a94442' }}
                                    type="number"
                                    className='form-control'
                                    bsSize='xs'
                                    name={`${x.allele}`}
                                    value={x.mfi}
                                    disabled={!alleleIds.includes(x.allele) || props.disabled}
                                    onChange={handleAlleleMfiChange}
                                />
                            </div>
                        ))}
                    </fieldset>
                ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 10 }}>
                <div>
                    <input
                        style={{ marginRight: '8px' }}
                        type="checkbox"
                        name="nonHla"
                        disabled={props.disabled}
                        checked={nonAlleleOptions.nonHla}
                        onChange={handleSetOtherOptions}
                    />
                </div>
                <div><label style={{ margin: 0, padding: 0 }}>Non-HLA</label></div>
            </div>

            {props.type == RejectionDSAType.Persistent && (
                <div style={{ display: 'flex', alignItems: 'center', paddingTop: 10 }}>
                    <div>
                        <input
                            style={{ marginRight: '8px' }}
                            type="checkbox"
                            checked={nonAlleleOptions.other}
                            id='other'
                            name="other"
                            disabled={props.disabled}
                            onChange={handleSetOtherOptions}
                        />
                    </div>
                    <div><label htmlFor='other' style={{ margin: 0, padding: 0 }}>Other:</label>&nbsp;</div>
                    <div style={{ width: '70%' }}>
                        <FormControl
                            style={{ borderColor: props.persistentDsaOtherValid ? undefined : '#a94442' }}
                            maxLength={250}
                            width={'80%'}
                            name="otherDetails"
                            value={nonAlleleOptions.otherDetails}
                            disabled={!nonAlleleOptions.other || props.disabled}
                            onChange={handleSetOtherOptionsForm}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default IdentifiedDSA;